<template>
  <div>
    <AdminNav />
    <main class="page">
      <header class="page-header">
        <div>
          <h2 class="page-title">Comptabilité micro</h2>
          <p class="subtitle">Comptabilité micro simplifiée — indicateurs à valider avec votre comptable.</p>
        </div>
      </header>

      <p class="notice">Comptabilité micro simplifiée — indicateurs à valider avec votre comptable.</p>
      <p class="error-message" v-if="erreur">{{ erreur }}</p>

      <nav class="tabs">
        <button v-for="tab in tabs" :key="tab.id" class="tab" :class="{ active: onglet === tab.id }" @click="onglet = tab.id">
          {{ tab.label }}
        </button>
      </nav>

      <section v-if="onglet === 'livre'" class="stack">
        <section class="panel">
          <div class="filters">
            <label class="field">Début<input v-model="periode.date_debut" class="input" type="date" @change="chargerLivre" /></label>
            <label class="field">Fin<input v-model="periode.date_fin" class="input" type="date" @change="chargerLivre" /></label>
            <button class="btn btn-primary" @click="exportCsv('livre-recettes')" :disabled="loading">Export CSV</button>
          </div>
        </section>

        <div class="kpis">
          <article class="kpi">
            <span>Total période</span>
            <strong>{{ formatMoney(livre.total) }}</strong>
          </article>
          <article class="kpi">
            <span>Paiements encaissés</span>
            <strong>{{ livre.nombre_paiements || 0 }}</strong>
          </article>
        </div>

        <section class="panel">
          <div class="panel-head">
            <h3>Livre des recettes</h3>
            <span class="badge">{{ recettes.length }}</span>
          </div>
          <div class="desktop-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Facture</th>
                  <th>Montant</th>
                  <th>Mode règlement</th>
                  <th>Référence</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in recettes" :key="row.facture_id + row.date_paiement + row.reference_paiement">
                  <td>{{ formatDate(row.date_paiement) }}</td>
                  <td>{{ row.client }}</td>
                  <td>{{ row.facture_numero }}</td>
                  <td>{{ formatMoney(row.montant) }}</td>
                  <td>{{ row.mode_reglement || 'À définir' }}</td>
                  <td>{{ row.reference_paiement || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mobile-list">
            <article v-for="row in recettes" :key="row.facture_id + row.date_paiement + row.montant" class="mobile-card">
              <div>
                <strong>{{ row.client }}</strong>
                <small>{{ formatDate(row.date_paiement) }} · {{ row.facture_numero }}</small>
              </div>
              <strong>{{ formatMoney(row.montant) }}</strong>
              <small>{{ row.mode_reglement || 'À définir' }} · {{ row.reference_paiement || 'Sans référence' }}</small>
            </article>
          </div>
          <p class="empty" v-if="!recettes.length">Aucun paiement encaissé sur la période.</p>
        </section>
      </section>

      <section v-if="onglet === 'declaration'" class="stack">
        <section class="panel">
          <div class="filters">
            <label class="field">Année<input v-model.number="annee" class="input" type="number" min="2000" max="2100" @change="chargerDeclaration" /></label>
            <button class="btn btn-primary" @click="exportCsv('declaration-ca')" :disabled="loading">Export CSV</button>
          </div>
        </section>
        <div class="kpis">
          <article class="kpi"><span>CA encaissé annuel</span><strong>{{ formatMoney(declaration.annuel) }}</strong></article>
          <article class="kpi"><span>CA mois courant</span><strong>{{ formatMoney(caMoisCourant) }}</strong></article>
          <article class="kpi"><span>CA trimestre courant</span><strong>{{ formatMoney(caTrimestreCourant) }}</strong></article>
          <article class="kpi"><span>Nombre paiements</span><strong>{{ declaration.nombre_paiements || 0 }}</strong></article>
        </div>
        <section class="panel">
          <h3>CA encaissé par mois</h3>
          <div class="desktop-table compact">
            <table>
              <thead><tr><th>Mois</th><th>CA encaissé</th><th>Paiements</th></tr></thead>
              <tbody>
                <tr v-for="row in declaration.mois || []" :key="row.mois">
                  <td>{{ nomMois(row.mois) }}</td>
                  <td>{{ formatMoney(row.ca_encaisse) }}</td>
                  <td>{{ row.nombre_paiements }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mobile-list">
            <article v-for="row in declaration.mois || []" :key="row.mois" class="mini-row">
              <span>{{ nomMois(row.mois) }}</span>
              <strong>{{ formatMoney(row.ca_encaisse) }}</strong>
              <small>{{ row.nombre_paiements }} paiements</small>
            </article>
          </div>
        </section>
        <section class="panel">
          <h3>CA encaissé par trimestre</h3>
          <div class="quarter-grid">
            <article v-for="row in declaration.trimestres || []" :key="row.trimestre" class="kpi small">
              <span>T{{ row.trimestre }}</span>
              <strong>{{ formatMoney(row.ca_encaisse) }}</strong>
              <small>{{ row.nombre_paiements }} paiements</small>
            </article>
          </div>
        </section>
      </section>

      <section v-if="onglet === 'tva'" class="stack">
        <section class="panel">
          <div class="filters">
            <label class="field">Année<input v-model.number="annee" class="input" type="number" min="2000" max="2100" @change="chargerTva" /></label>
          </div>
        </section>
        <section class="panel">
          <div class="panel-head">
            <h3>Suivi TVA</h3>
            <span class="status" :class="tva.alerte">{{ libelleAlerteTva }}</span>
          </div>
          <p class="subtitle">Suivi indicatif des seuils TVA, à valider avec le comptable.</p>
          <div class="threshold">
            <div class="threshold-head"><span>Seuil base prestations de services</span><strong>37 500 €</strong></div>
            <div class="progress"><span :style="{ width: progressWidth(tva.pourcentage_utilisation_seuil_base) }"></span></div>
            <small>{{ formatPercent(tva.pourcentage_utilisation_seuil_base) }} utilisé</small>
          </div>
          <div class="threshold">
            <div class="threshold-head"><span>Seuil majoré prestations de services</span><strong>41 250 €</strong></div>
            <div class="progress major"><span :style="{ width: progressWidth(tva.pourcentage_utilisation_seuil_majore) }"></span></div>
            <small>{{ formatPercent(tva.pourcentage_utilisation_seuil_majore) }} utilisé</small>
          </div>
          <div class="alert-box" :class="tva.alerte">
            CA encaissé annuel : {{ formatMoney(tva.ca_encaisse_annuel) }} · {{ libelleAlerteTva }}
          </div>
        </section>
      </section>

      <section v-if="onglet === 'exports'" class="stack">
        <section class="panel export-grid">
          <button class="btn btn-primary" @click="exportCsv('livre-recettes')" :disabled="loading">Export livre des recettes</button>
          <button class="btn btn-primary" @click="exportCsv('declaration-ca')" :disabled="loading">Export déclaration CA</button>
          <button class="btn btn-secondary" @click="exportCsv('charges')" :disabled="loading">Export charges</button>
        </section>
        <p class="notice">Conservez vos justificatifs et exports pendant 10 ans.</p>
      </section>

      <section v-if="onglet === 'justificatifs'" class="panel checklist">
        <label v-for="item in checklist" :key="item.id" class="check-row">
          <input v-model="item.checked" type="checkbox" />
          <span>{{ item.label }}</span>
        </label>
      </section>
    </main>
  </div>
</template>

<script>
import AdminNav from '../../components/AdminNav.vue'
import adminApi from '../../adminApi'

export default {
  name: 'AdminComptabiliteMicro',
  components: { AdminNav },
  data() {
    return {
      tabs: [
        { id: 'livre', label: 'Livre des recettes' },
        { id: 'declaration', label: 'Déclaration CA' },
        { id: 'tva', label: 'Suivi TVA' },
        { id: 'exports', label: 'Exports' },
        { id: 'justificatifs', label: 'Justificatifs' }
      ],
      onglet: 'livre',
      loading: false,
      erreur: '',
      annee: new Date().getFullYear(),
      periode: this.periodeDefaut(),
      livre: {},
      declaration: {},
      tva: {},
      checklist: [
        { id: 'factures', label: 'Factures clients générées', checked: false },
        { id: 'paiements', label: 'Paiements enregistrés', checked: false },
        { id: 'charges', label: 'Charges saisies', checked: false },
        { id: 'exports', label: 'Exports CSV téléchargés', checked: false },
        { id: 'justificatifs', label: 'Justificatifs conservés', checked: false }
      ]
    }
  },
  computed: {
    recettes() { return this.livre.recettes || [] },
    caMoisCourant() {
      const mois = new Date().getMonth() + 1
      return (this.declaration.mois || []).find(row => row.mois === mois)?.ca_encaisse || 0
    },
    caTrimestreCourant() {
      const trimestre = Math.floor(new Date().getMonth() / 3) + 1
      return (this.declaration.trimestres || []).find(row => row.trimestre === trimestre)?.ca_encaisse || 0
    },
    libelleAlerteTva() {
      return {
        normal: 'Situation normale',
        attention: 'Attention à partir de 80 %',
        proche_seuil: 'Proche du seuil',
        depasse_seuil_base: 'Seuil base dépassé',
        depasse_seuil_majore: 'Seuil majoré dépassé'
      }[this.tva.alerte] || 'Situation normale'
    }
  },
  methods: {
    periodeDefaut() {
      const now = new Date()
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10)
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10)
      return { date_debut: start, date_fin: end }
    },
    formatMoney(value) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value || 0))
    },
    formatDate(value) { return value ? new Date(value).toLocaleDateString('fr-FR') : '—' },
    formatPercent(value) { return `${Number(value || 0).toFixed(1).replace('.', ',')} %` },
    progressWidth(value) { return `${Math.min(100, Math.max(0, Number(value || 0)))}%` },
    nomMois(value) {
      return new Date(Date.UTC(this.annee, Number(value) - 1, 1)).toLocaleDateString('fr-FR', { month: 'long' })
    },
    async chargerLivre() {
      const res = await adminApi.get('/comptabilite-micro/livre-recettes', { params: this.periode })
      this.livre = res.data || {}
    },
    async chargerDeclaration() {
      const res = await adminApi.get('/comptabilite-micro/declaration-ca', { params: { annee: this.annee } })
      this.declaration = res.data || {}
      await this.chargerTva()
    },
    async chargerTva() {
      const res = await adminApi.get('/comptabilite-micro/suivi-tva', { params: { annee: this.annee } })
      this.tva = res.data || {}
    },
    async exportCsv(type) {
      this.loading = true
      this.erreur = ''
      try {
        const config = { responseType: 'blob', params: {} }
        let url = '/comptabilite-micro/export/livre-recettes.csv'
        if (type === 'declaration-ca') {
          url = '/comptabilite-micro/export/declaration-ca.csv'
          config.params = { annee: this.annee }
        } else if (type === 'charges') {
          url = '/comptabilite-micro/export/charges.csv'
          config.params = this.periode
        } else {
          config.params = this.periode
        }
        const res = await adminApi.get(url, config)
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }))
        const link = document.createElement('a')
        const header = res.headers['content-disposition'] || ''
        const match = header.match(/filename="([^"]+)"/)
        link.href = blobUrl
        link.download = match ? match[1] : `${type}.csv`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Export CSV impossible.'
      } finally {
        this.loading = false
      }
    },
    async chargerInitial() {
      await Promise.all([this.chargerLivre(), this.chargerDeclaration()])
    }
  },
  mounted() {
    this.chargerInitial().catch(err => {
      this.erreur = err.response?.data?.error || 'Impossible de charger la comptabilité micro.'
    })
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px;
  box-sizing: border-box;
}
.page-header {
  display: grid;
  gap: 12px;
  margin-bottom: 12px;
}
.page-title {
  margin: 0 0 4px;
  color: #1a202c;
  font-size: 22px;
  font-weight: 700;
}
.subtitle, .field, small {
  margin: 0;
  color: #718096;
  font-size: 13px;
  line-height: 1.4;
}
.notice {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7fafc;
  color: #2d3748;
  font-size: 13px;
  font-weight: 700;
}
.error-message {
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  padding: 10px 12px;
  border-radius: 8px;
}
.tabs, .filters, .stack, .kpis, .quarter-grid, .export-grid {
  display: grid;
  gap: 10px;
}
.tabs {
  grid-template-columns: 1fr;
  margin-bottom: 14px;
}
.tab, .btn {
  min-height: 40px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}
.tab {
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
}
.tab.active {
  background: #2d3748;
  color: white;
  border-color: #2d3748;
}
.panel, .kpi {
  min-width: 0;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.panel {
  padding: 14px;
}
.panel h3 {
  margin: 0 0 12px;
  color: #2d3748;
  font-size: 16px;
}
.panel-head, .threshold-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.kpis, .quarter-grid {
  grid-template-columns: 1fr;
}
.kpi {
  padding: 14px;
  display: grid;
  gap: 6px;
}
.kpi span {
  color: #718096;
  font-size: 12px;
  font-weight: 700;
}
.kpi strong {
  color: #1a202c;
  font-size: 22px;
}
.kpi.small strong {
  font-size: 18px;
}
.field {
  display: grid;
  gap: 5px;
  min-width: 0;
  font-weight: 700;
}
.input {
  width: 100%;
  min-height: 40px;
  padding: 9px 11px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;
}
.btn {
  border: none;
  padding: 9px 12px;
}
.btn-primary {
  background: #2d3748;
  color: white;
}
.btn-secondary {
  background: #edf2f7;
  color: #2d3748;
}
.desktop-table {
  display: none;
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
th, td {
  padding: 10px;
  border-bottom: 1px solid #edf2f7;
  text-align: left;
  white-space: nowrap;
}
th {
  color: #4a5568;
  background: #f7fafc;
}
.mobile-list {
  display: grid;
  gap: 8px;
}
.mobile-card, .mini-row, .check-row {
  border: 1px solid #edf2f7;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}
.mobile-card {
  display: grid;
  gap: 8px;
}
.mobile-card div, .mini-row {
  display: grid;
  gap: 3px;
}
.mini-row {
  grid-template-columns: 1fr auto;
  align-items: center;
}
.mini-row small {
  grid-column: 1 / -1;
}
.badge, .status {
  border-radius: 999px;
  background: #edf2f7;
  color: #2d3748;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
}
.status.attention, .status.proche_seuil {
  background: #fffaf0;
  color: #b7791f;
}
.status.depasse_seuil_base, .status.depasse_seuil_majore {
  background: #fff5f5;
  color: #c53030;
}
.threshold {
  display: grid;
  gap: 8px;
  margin-top: 16px;
}
.progress {
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf2f7;
}
.progress span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #3182ce;
}
.progress.major span {
  background: #805ad5;
}
.alert-box {
  margin-top: 16px;
  border-radius: 8px;
  padding: 12px;
  background: #f0fff4;
  color: #276749;
  font-weight: 700;
}
.alert-box.attention, .alert-box.proche_seuil {
  background: #fffaf0;
  color: #975a16;
}
.alert-box.depasse_seuil_base, .alert-box.depasse_seuil_majore {
  background: #fff5f5;
  color: #c53030;
}
.checklist {
  display: grid;
  gap: 8px;
}
.check-row {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 10px;
  color: #2d3748;
  font-weight: 700;
}
.empty {
  margin: 12px 0 0;
  color: #718096;
  font-size: 13px;
}
@media (min-width: 720px) {
  .tabs { grid-template-columns: repeat(5, minmax(0, 1fr)); }
  .filters { grid-template-columns: repeat(3, minmax(0, 1fr)); align-items: end; }
  .kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .quarter-grid, .export-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .desktop-table { display: block; }
  .mobile-list { display: none; }
}
</style>
